import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import Button from '@/components/ui/Button';
import { IntegrationDemo } from '../demoStore';
import { isSourceRemovable, pointsUsingSource, summariseSources } from '../selectors';
import AddSourceWizard from './AddSourceWizard';
import SourceCard from './SourceCard';

interface ConfigurationTabProps {
  demo: IntegrationDemo;
  actorName: string;
  onViewExchange: (sourceId: string) => void;
}

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({ demo, actorName, onViewExchange }) => {
  const { t } = useTranslation();
  const [wizardOpen, setWizardOpen] = useState(false);

  const summary = useMemo(() => summariseSources(demo.state.sources), [demo.state.sources]);

  return (
    <div className="space-y-6">
      <section className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">{t('extInt.config.heading')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('extInt.config.subheading')}</p>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="config-summary">
            {t('extInt.config.summary', { connected: summary.connected, errored: summary.errored })}
          </p>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="config-mowazaf-note">
            {t('extInt.config.mowazafNote')}
          </p>
        </div>
        <Button onClick={() => setWizardOpen(true)} data-testid="add-source-btn">
          <Plus className="me-2 h-4 w-4" aria-hidden="true" />
          {t('extInt.config.addSource')}
        </Button>
      </section>

      <div className="rounded-lg border border-border bg-card">
        {demo.state.sources.map((source) => (
          <SourceCard
            key={source.id}
            source={source}
            removable={isSourceRemovable(source.id, demo.state.points, demo.state.sources)}
            blockedByCount={pointsUsingSource(source.id, demo.state.points).length}
            testing={demo.busyKey === `test:${source.id}`}
            onTest={() => { void demo.testSource(source.id, actorName); }}
            onRemove={() => demo.removeSource(source.id, actorName)}
            onViewExchange={() => onViewExchange(source.id)}
          />
        ))}
        {summary.total === 0 && (
          <p
            className="border-t border-border px-3 py-4 text-sm text-muted-foreground"
            data-testid="config-empty"
          >
            {t('extInt.config.empty')}
          </p>
        )}
      </div>

      <AddSourceWizard
        open={wizardOpen}
        busy={demo.busyKey === 'addSource'}
        onCancel={() => setWizardOpen(false)}
        onAdd={async (input) => {
          await demo.addSource(input, actorName);
          setWizardOpen(false);
        }}
      />
    </div>
  );
};

export default ConfigurationTab;
